/**
 * InstructorTrustStack — unified trust badges for an instructor.
 *
 * Pulls /api/instructor-trust/{id} and renders all earned chips in a
 * canonical order so every public surface (marketplace card, public
 * profile, booking page, pupil invite, dashboard header, chat) shows
 * the SAME stack.
 *
 * Order of precedence (left → right):
 *   1. Identity Verified         — email + phone confirmed
 *   2. Verified ADI / Verified PDI — DVSA card check
 *   3. Standards tier             — Bronze → Black
 *   4. Teaching Excellence        — AI-review derived (placeholder until module ships)
 *   5. PDI Progress               — trainee development tier (placeholder)
 *   6. Trusted Pro                — paid plan + complete profile
 *
 * Props:
 *   instructorId  — required; the user document _id
 *   trust         — optional pre-fetched object (skips the network call)
 *   size          — 'sm' | 'md'  (default 'sm')
 *   max           — limit visible chips, rest collapse to "+N" (default 6)
 *   className     — additional wrapper classes
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ShieldCheck, BadgeCheck, GraduationCap, Trophy, Sparkles,
  CheckCircle2, ChevronRight,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_STYLE = {
  bronze:   { fg: "text-amber-700",   bg: "bg-amber-100   dark:bg-amber-900/25",   ring: "ring-amber-200/60" },
  silver:   { fg: "text-slate-600",   bg: "bg-slate-200   dark:bg-slate-700/40",   ring: "ring-slate-300/60" },
  gold:     { fg: "text-yellow-700",  bg: "bg-yellow-100  dark:bg-yellow-900/25",  ring: "ring-yellow-300/60" },
  platinum: { fg: "text-cyan-700",    bg: "bg-cyan-100    dark:bg-cyan-900/25",    ring: "ring-cyan-300/60" },
  black:    { fg: "text-white",       bg: "bg-slate-900   dark:bg-slate-800",      ring: "ring-slate-700" },
};

function buildChips(t) {
  if (!t) return [];
  const chips = [];

  // 1. Identity
  if (t.identity?.label) {
    chips.push({
      key: "identity",
      label: t.identity.label,
      icon: ShieldCheck,
      tone: t.identity.level === "full" ? "emerald" : "indigo",
      title: t.identity.level === "full"
        ? "Email and phone confirmed."
        : "Email confirmed. Phone verification coming soon.",
    });
  }

  // 2. Professional ADI/PDI
  const prof = t.professional;
  if (prof?.verified) {
    const isPdi = prof.badge_type === "pdi";
    chips.push({
      key: "professional",
      label: prof.chip_label || (isPdi ? "Verified PDI" : "Verified ADI"),
      icon: BadgeCheck,
      tone: isPdi ? "violet" : "blue",
      title: prof.expiry_date ? `DVSA card verified · expires ${prof.expiry_date}` : "DVSA card verified by admin.",
    });
  }

  // 3. Standards tier
  const st = t.standards;
  if (st?.tier && st.tier !== "none" && st.tier_label) {
    chips.push({
      key: "standards",
      label: st.tier_label,
      icon: Trophy,
      tone: "tier",
      tier: st.tier,
      title: `Auto-computed from your ongoing standards activity. Score ${st.score ?? "—"}.`,
    });
  }

  // 4. Teaching excellence — only if backend serves it
  if (t.teaching_excellence?.label) {
    const teTier = t.teaching_excellence.tier;
    chips.push({
      key: "teaching",
      label: t.teaching_excellence.label,
      icon: Sparkles,
      tone: teTier && TIER_STYLE[teTier] ? "tier" : "amber",
      tier: teTier,
      title: typeof t.teaching_excellence.score === "number"
        ? `Average lesson-review score ${t.teaching_excellence.score} across ${t.teaching_excellence.review_count || 0} reviews.`
        : "Earned through AI lesson reviews and consistent quality scores.",
    });
  }

  // 5. PDI progress (optional)
  if (t.pdi_progress?.label) {
    chips.push({
      key: "pdi-progress",
      label: t.pdi_progress.label,
      icon: GraduationCap,
      tone: "indigo",
      title: "Active PDI pathway — training in progress.",
    });
  }

  // 6. Trusted Pro
  if (t.trusted_pro?.active) {
    chips.push({
      key: "trusted",
      label: t.trusted_pro.label,
      icon: CheckCircle2,
      tone: "emerald",
      title: "Active subscriber with a complete profile.",
    });
  }

  return chips;
}

const TONE_CLASSES = {
  emerald: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 ring-emerald-200/60",
  indigo:  "bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 ring-indigo-200/60",
  blue:    "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 ring-blue-200/60",
  violet:  "bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 ring-violet-200/60",
  amber:   "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 ring-amber-200/60",
};

function chipClasses(chip, size) {
  const sizeCls = size === "md"
    ? "text-[12px] px-2.5 py-1 gap-1.5"
    : "text-[11px] px-2 py-0.5 gap-1";

  if (chip.tone === "tier" && chip.tier && TIER_STYLE[chip.tier]) {
    const t = TIER_STYLE[chip.tier];
    return `inline-flex items-center rounded-full ring-1 font-bold ${t.bg} ${t.fg} ${t.ring} ${sizeCls}`;
  }
  return `inline-flex items-center rounded-full ring-1 font-bold ${TONE_CLASSES[chip.tone] || TONE_CLASSES.indigo} ${sizeCls}`;
}

export default function InstructorTrustStack({ instructorId, trust = null, size = "sm", max = 6, className = "" }) {
  const [data, setData] = useState(trust);
  const [loading, setLoading] = useState(!trust);

  useEffect(() => {
    if (trust) { setData(trust); setLoading(false); return; }
    if (!instructorId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await axios.get(`${API}/api/instructor-trust/${instructorId}`, { withCredentials: true });
        if (!cancelled) setData(d);
      } catch { /* silent — we just hide the stack */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [instructorId, trust]);

  const chips = useMemo(() => buildChips(data), [data]);
  if (loading || chips.length === 0) return null;

  const visible = chips.slice(0, max);
  const hidden = chips.slice(max);
  const iconSize = size === "md" ? "w-3.5 h-3.5" : "w-3 h-3";

  return (
    <div data-testid="instructor-trust-stack" className={`flex flex-wrap items-center gap-1.5 ${className}`}>
      {visible.map((c) => (
        <span key={c.key} title={c.title} data-testid={`trust-chip-${c.key}`} className={chipClasses(c, size)}>
          <c.icon className={iconSize} />
          {c.label}
        </span>
      ))}
      {hidden.length > 0 && (
        <span
          data-testid="trust-chip-more"
          title={hidden.map((h) => h.label).join(" · ")}
          className="inline-flex items-center rounded-full ring-1 font-bold bg-slate-100 text-slate-600 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 text-[11px] px-2 py-0.5"
        >
          +{hidden.length}
          <ChevronRight className="w-2.5 h-2.5 ml-0.5" />
        </span>
      )}
    </div>
  );
}

/** Helper for surfaces that already fetch trust info — render directly. */
export function InstructorTrustStackInline({ trust, size = "sm", max = 6, className = "" }) {
  return <InstructorTrustStack instructorId={trust?.instructor_id} trust={trust} size={size} max={max} className={className} />;
}
